<template>
  <div class="hero-section">
    <ShopDefaultHeroSection v-if="heroSectionToShow === 'default'" />
    <PremiumMonthlyPromoSection v-if="heroSectionToShow === 'premium-monthly'" />
    <EDAPremiumPreSaleSection v-if="heroSectionToShow === 'eeda-presale'" />
    <BooGoHeroSection v-if="heroSectionToShow === 'boogo'" />
    <BlackFridayHeroSection v-if="heroSectionToShow === 'black-friday'" />
    <EwptxPreSaleSection v-if="heroSectionToShow === 'ewptx-presale'" />
    <LeapYearHeroSection v-if="heroSectionToShow === 'leap-year-2024'" />
    <TCSPromoHeroSection v-if="heroSectionToShow === 'tcs-promo'" />
    <FlashSaleHeroSection v-if="heroSectionToShow === 'flash-sale'" />
    <PremiumPlusPromoHeroSection v-if="heroSectionToShow === 'premium-plus'" />
    <SpringHeroSection v-if="heroSectionToShow === 'spring'" />
    <MaySubscriptionHeroSection v-if="heroSectionToShow === 'may-sale'" />
    <MayFlashSaleHeroSection v-if="heroSectionToShow === 'may-flash-sale'" />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import PremiumMonthlyPromoSection from '@/components/hero-sections/PremiumMonthlyPromoSection.vue'
import EDAPremiumPreSaleSection from '@/components/hero-sections/EDAPremiumPreSaleSection.vue'
import ShopDefaultHeroSection from '@/components/hero-sections/ShopDefaultHeroSection.vue'
import BooGoHeroSection from '@/components/hero-sections/BooGoHeroSection.vue'
import BlackFridayHeroSection from '@/components/hero-sections/BlackFridayHeroSection.vue'
import LeapYearHeroSection from '@/components/hero-sections/LeapYearHeroSection.vue'
import { routesRules } from '@/helpers/mixins'
import EwptxPreSaleSection from '@/components/hero-sections/EwptxPreSaleSection.vue'
import TCSPromoHeroSection from '@/components/hero-sections/TCSPromoHeroSection.vue'
import FlashSaleHeroSection from '@/components/hero-sections/FlashSaleHeroSection.vue'
import PremiumPlusPromoHeroSection from '@/components/hero-sections/PremiumPlusPromoHeroSection.vue'
import SpringHeroSection from '@/components/hero-sections/SpringHeroSection.vue'
import MaySubscriptionHeroSection from '../hero-sections/MaySubscriptionHeroSection.vue'
import MayFlashSaleHeroSection from '../hero-sections/MayFlashSaleHeroSection.vue'

export default {
  name: 'HeroSection',
  emits: ['hero-section-to-show'],
  mixins: [routesRules],
  components: {
    PremiumMonthlyPromoSection,
    EDAPremiumPreSaleSection,
    EwptxPreSaleSection,
    ShopDefaultHeroSection,
    BooGoHeroSection,
    BlackFridayHeroSection,
    LeapYearHeroSection,
    TCSPromoHeroSection,
    FlashSaleHeroSection,
    PremiumPlusPromoHeroSection,
    SpringHeroSection,
    MaySubscriptionHeroSection,
    MayFlashSaleHeroSection,
  },
  data() {
    return {
      heroSectionToShow: 'default',
    }
  },
  computed: {
    ...mapState({
      isOnePlanAvailable: state => state.products.isOnePlanAvailable,
      showGenericBackground: state => state.app.showGenericBackground,
      isPremiumMonthlyPromoActive: state => state.app.isPremiumMonthlyPromoActive,
      isEDAPremiumPreSaleActive: state => state.app.isEDAPremiumPreSaleActive,
      isAuthenticated: state => state.auth.isAuthenticated,
      userHasSubscription: state => state.subscriptions.userHasSubscription,
      usersCurrentSubscriptions: state => state.subscriptions.usersCurrentSubscriptions,
    }),
    ...mapGetters({
      isBooGoPromoActive: 'app/getIsBooGoPromoActive',
      isBlackFridayActive: 'app/isBlackFridayActive',
      isPreSaleActive: 'app/getRunningPreSale',
      isLeapYearPromoActive: 'app/isLeapYearPromoActive',
      isBusinessUser: 'business/isBusinessUser',
      isTCSPromoEnabled: 'featureFlags/isTCSPromoEnabled',
      isFlashSaleActive: 'featureFlags/isFlashSaleActive',
      premiumPlusEnabled: 'featureFlags/premiumPlusEnabled',
      isPremiumPlusEnabledForUser: 'subscriptions/isPremiumPlusEnabledForUser',
      userHasSkillDive: 'subscriptions/userHasSkillDive',
      isSpringPromoEnabled: 'featureFlags/isSpringPromoEnabled',
      isMaySaleEnabled: 'featureFlags/isMaySaleEnabled',
      isMayFlashSaleEnabled: 'featureFlags/isMayFlashSaleEnabled',
    }),
    heroSectionToggler() {
      return [
        {
          name: 'eeda-presale',
          validation: [
            !this.isOnePlanAvailable,
            this.isEDAPremiumPreSaleActive,
            this.isShopPage || this.isEDAPremiumPage,
          ],
        },
        {
          name: 'premium-monthly',
          validation: [
            !this.isOnePlanAvailable,
            this.isPremiumMonthlyPromoActive,
            this.isShopPage || this.isPremiumMonthlyPromoPage,
          ],
        },
        {
          name: 'boogo',
          validation: [
            [
              !this.isAuthenticated,
              !this.isOnePlanAvailable,
              this.isBooGoPromoActive,
              this.isShopPage || this.isBooGoPage,
            ],
            [
              this.isAuthenticated,
              !this.isOnePlanAvailable,
              this.isBooGoPromoActive,
              !(this.usersCurrentSubscriptions.length > 0),
              this.userHasSkillDive,
              this.isShopPage || this.isBooGoPage,
            ],
          ],
        },
        {
          name: 'black-friday',
          validation: [
            !this.isBusinessUser,
            !this.isOnePlanAvailable,
            this.isBlackFridayActive,
            this.isShopPage || this.isBlackFridayPage,
          ],
        },
        {
          name: 'leap-year-2024',
          validation: [
            !this.isOnePlanAvailable,
            this.isLeapYearPromoActive,
            this.isShopPage || this.isLeapYearPromoPage,
          ],
        },
        {
          name: 'ewptx-presale',
          validation: [!this.userHasSubscription, this.isPreSaleActive],
        },
        {
          name: 'tcs-promo',
          validation: [!this.isOnePlanAvailable, this.isTCSPromoEnabled, this.isShopPage],
        },
        {
          name: 'flash-sale',
          validation: [!this.isOnePlanAvailable, this.isFlashSaleActive, this.isShopPage],
        },
        {
          name: 'spring',
          validation: [!this.isOnePlanAvailable, this.isSpringPromoEnabled, this.isShopPage],
        },
        {
          name: 'premium-plus',
          validation: [!this.isOnePlanAvailable, this.isPremiumPlusEnabledForUser, this.isShopPage],
        },
        {
          name: 'may-sale',
          validation: [
            !this.isOnePlanAvailable,
            !this.isBusinessUser,
            this.isMaySaleEnabled,
            this.isShopPage,
          ],
        },
        {
          name: 'may-flash-sale',
          validation: [
            !this.isOnePlanAvailable,
            !this.isBusinessUser,
            this.isMayFlashSaleEnabled,
            this.isShopPage,
          ],
        },
        {
          name: 'default',
          validation: [this.showGenericBackground],
        },
      ]
    },
    watchVariables() {
      return {
        isBlackFridayActive: this.isBlackFridayActive,
        isTCSPromoEnabled: this.isTCSPromoEnabled,
        isPreSaleActive: this.isPreSaleActive,
        isAuthenticated: this.isAuthenticated,
        showGenericBackground: this.showGenericBackground,
        isFlashSaleActive: this.isFlashSaleActive,
        isSpringPromoEnabled: this.isSpringPromoEnabled,
        isPremiumPlusEnabledForUser: this.isPremiumPlusEnabledForUser,
        isMaySaleEnabled: this.isMaySaleEnabled,
        isMayFlashSaleEnabled: this.isMayFlashSaleEnabled,
      }
    },
  },
  methods: {
    setHeroSectionToShow() {
      const heroHeaderValue = this.heroSectionToggler.find(heroHeader => {
        if (heroHeader.validation[0].length > 1) {
          const validations = heroHeader.validation.find(validationsBlock =>
            validationsBlock.every(v => v === true)
          )
          return !!validations
        }
        return heroHeader.validation.every(v => v === true)
      })
      const heroHeaderOutput = heroHeaderValue ? heroHeaderValue.name : 'default'
      return !this.isShopPage ? '' : heroHeaderOutput
    },
    updateHeroSection() {
      this.heroSectionToShow = this.setHeroSectionToShow()
      this.$emit('hero-section-to-show', this.heroSectionToShow)
    },
  },
  created() {
    this.updateHeroSection()
    this.$watch(
      () => this.watchVariables,
      (newVal, oldVal) => {
        if (newVal !== oldVal) {
          this.updateHeroSection()
        }
      },
      { deep: true }
    )
  },
  beforeDestroy() {
    this.heroSectionToShow = ''
    this.$emit('hero-section-to-show', this.heroSectionToShow)
  },
}
</script>

<style lang="scss" scoped>
.hero-section {
  display: block;
}
</style>
